import './App.css';
import Builder from './components/pages/builder';
import React, { useEffect, useState } from 'react';
import { CometChat } from '@cometchat/chat-sdk-javascript';
import { CometChatUIKitLoginListener } from '@cometchat/chat-uikit-react';
import { CometChatProvider, CometChatSettingsInterface } from './sampleApp/src/context/CometChatContext';

interface VcbAppProps {
  /** Indicates if the user is authenticated. */
  isAuthenticated?: boolean;
  /** The builder settings for authentication. */
  authBuilderSetting?: CometChatSettingsInterface;
  /** Callback when builder settings change. */
  onValuesChanged?: (value?: CometChatSettingsInterface) => void;
  /** Callback when the modal's "OK" button is clicked. */
  modalCallbackOnOk?: () => void;
  /** Callback for when the dashboard button is clicked. */
  onDashboardClick?: () => void;
  /** Callback function for handling downloads. */
  onDownloadCallBack?: (input: string) => void;
  /** Callback function triggered when the reset button is clicked. */
  onResetClick?: () => void;
}

/**
 * Main application component for the VCB app.
 *
 * @param {VcbAppProps} props - The component props.
 * @returns {JSX.Element} The rendered App component.
 */
function App({
  isAuthenticated = false,
  authBuilderSetting,
  onValuesChanged,
  modalCallbackOnOk,
  onDashboardClick,
  onDownloadCallBack,
  onResetClick,
}: Readonly<VcbAppProps>) {
  const [loggedInUser, setLoggedInUser] = useState<CometChat.User | null>(null);
  const getLoggedInUser = CometChatUIKitLoginListener?.getLoggedInUser();

  /**
   * Sets up a CometChat login listener to update the logged-in user state.
   * Removes the listener on component unmount.
   */
  useEffect(() => {
    CometChat.addLoginListener(
      'vcb-app',
      new CometChat.LoginListener({
        loginSuccess: (user: CometChat.User) => {
          setLoggedInUser(user);
        },
        logoutSuccess: () => {
          setLoggedInUser(null);
        },
      })
    );

    return () => CometChat.removeLoginListener('vcb-app');
  }, []);

  /**
   * Updates the logged-in user state when `getLoggedInUser` changes.
   */
  useEffect(() => {
    setLoggedInUser(getLoggedInUser);
  }, [getLoggedInUser]);

  return (
    <CometChatProvider authBuilderSetting={authBuilderSetting}>
      <div id="vcbApp" className="vcbApp">
        {loggedInUser && (
          <Builder
            isAuthenticated={isAuthenticated}
            onValuesChanged={onValuesChanged}
            authBuilderSetting={authBuilderSetting}
            modalCallbackOnOk={modalCallbackOnOk}
            onDashboardClick={onDashboardClick}
            onDownloadCallBack={onDownloadCallBack}
            onResetClick={onResetClick}
          />
        )}
      </div>
    </CometChatProvider>
  );
}

export default React.memo(App);
